import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import logsAPI from '../../services/logs';
import useDialogFocus from '../../hooks/useDialogFocus';
import '../Dashboard/Dashboard.css';
import './SystemLogs.css';

const PAGE_SIZE = 25;

const ROLES = [
  'SuperAdmin', 'Admin', 'AdminEvent', 'BoothAdmin', 'Recruiter',
  'Interpreter', 'GlobalInterpreter', 'Support', 'GlobalSupport', 'JobSeeker'
];

const METHOD_LABELS = { POST: 'Create (POST)', PUT: 'Update (PUT)', PATCH: 'Update (PATCH)', DELETE: 'Delete (DELETE)' };

const SOURCE_LABELS = {
  server: 'Server error',
  request: 'Failed request (5xx)',
  process: 'Crash (uncaught)'
};

const CONFIG = {
  activity: {
    title: 'Activity Logs',
    sidebarKey: 'logs-activity',
    noun: 'activity entries',
    caption: 'Activity log entries, newest first',
    searchHint: 'Name, email, action, booth, event, job seeker, path or IP address',
    intro: 'Every create, update and delete request made through the platform, plus every sign-in attempt '
      + '(including failed ones). Request contents such as passwords are never recorded.',
    load: logsAPI.getActivityLogs,
    emptyFilters: { q: '', role: '', method: '', outcome: '', from: '', to: '' }
  },
  errors: {
    title: 'Error Logs',
    sidebarKey: 'logs-errors',
    noun: 'errors',
    caption: 'Server errors, newest first',
    searchHint: 'Message, path, email or server name',
    intro: 'Errors recorded by the server: failures inside requests, requests that answered with a server error, '
      + 'and crashes. Errors from every server instance appear here and are kept across deploys.',
    load: logsAPI.getErrorLogs,
    emptyFilters: { q: '', source: '', from: '', to: '' }
  }
};

function formatDateTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}

/** Date inputs give local calendar days; the API wants instants covering the whole day. */
function toQuery(filters, page) {
  const params = { ...filters, page, limit: PAGE_SIZE };
  if (filters.from) params.from = new Date(`${filters.from}T00:00:00`).toISOString();
  if (filters.to) params.to = new Date(`${filters.to}T23:59:59.999`).toISOString();
  return params;
}

function describeResults(pagination, noun) {
  if (!pagination) return '';
  const { page, limit, total, totalPages } = pagination;
  if (total === 0) return `No ${noun} match these filters.`;
  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);
  return `Showing ${first} to ${last} of ${total} ${noun}, page ${page} of ${totalPages}.`;
}

function UserCell({ entry }) {
  if (!entry.userName && !entry.userEmail) return <span className="system-logs-muted">Not signed in</span>;
  return (
    <>
      {entry.userName && <span className="system-logs-strong">{entry.userName}</span>}
      {entry.userName && entry.userEmail && <br />}
      {entry.userEmail && <span className="system-logs-break">{entry.userEmail}</span>}
    </>
  );
}

function RequestCell({ entry }) {
  if (!entry.method && !entry.path) return <span className="system-logs-muted">None</span>;
  return (
    <span className="system-logs-break">
      <span className="system-logs-method">{entry.method}</span> {entry.path}
    </span>
  );
}

function ActivityTable({ entries, captionId, caption }) {
  return (
    <table className="system-logs-table">
      <caption id={captionId}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">User</th>
          <th scope="col">Role</th>
          <th scope="col">Organization</th>
          <th scope="col">Action</th>
          <th scope="col">Request</th>
          <th scope="col">Result</th>
          <th scope="col">IP address</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry._id}>
            <td><time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time></td>
            <td>
              <UserCell entry={entry} />
              {entry.impersonatorUserId && !entry.details?.startsWith('Done by') && (
                <><br /><span className="system-logs-muted">(while impersonating)</span></>
              )}
            </td>
            <td>{entry.role || <span className="system-logs-muted">None</span>}</td>
            <td>{entry.organizationId?.name || <span className="system-logs-muted">None</span>}</td>
            <td>
              <span className="system-logs-strong">{entry.action}</span>
              {entry.details && (
                <ul className="system-logs-facts">
                  {entry.details.split(' · ').map((fact) => <li key={fact}>{fact}</li>)}
                </ul>
              )}
            </td>
            <td><RequestCell entry={entry} /></td>
            <td>
              <span className={`system-logs-badge system-logs-badge--${entry.outcome}`}>
                {entry.outcome === 'success' ? 'Succeeded' : 'Failed'}
              </span>{' '}
              <span className="system-logs-muted">({entry.statusCode})</span>
            </td>
            <td className="system-logs-break">{entry.ip || <span className="system-logs-muted">Unknown</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ErrorTable({ entries, captionId, caption }) {
  return (
    <table className="system-logs-table">
      <caption id={captionId}>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Type</th>
          <th scope="col">Message</th>
          <th scope="col">Request</th>
          <th scope="col">User</th>
          <th scope="col">Server</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry._id}>
            <td><time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time></td>
            <td>
              {SOURCE_LABELS[entry.source] || entry.source}
              {entry.level && entry.level !== 'error' && (
                <><br /><span className="system-logs-muted">Level: {entry.level}</span></>
              )}
            </td>
            <td className="system-logs-message">
              <span className="system-logs-break">{entry.message}</span>
              {entry.stack && (
                <details className="system-logs-details">
                  <summary>
                    Stack trace<span className="sr-only"> for the error at {formatDateTime(entry.createdAt)}</span>
                  </summary>
                  <pre>{entry.stack}</pre>
                </details>
              )}
            </td>
            <td>
              <RequestCell entry={entry} />
              {entry.statusCode && <><br /><span className="system-logs-muted">Status {entry.statusCode}</span></>}
            </td>
            <td>
              <UserCell entry={entry} />
              {entry.role && <><br /><span className="system-logs-muted">{entry.role}</span></>}
            </td>
            <td className="system-logs-break">
              {entry.host || <span className="system-logs-muted">Unknown</span>}
              {entry.environment && <><br /><span className="system-logs-muted">{entry.environment}</span></>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SystemLogs({ type = 'activity' }) {
  const config = CONFIG[type] || CONFIG.activity;
  const idPrefix = useId();
  const fieldId = (name) => `${idPrefix}-${name}`;
  const captionId = fieldId('caption');

  const [draft, setDraft] = useState(config.emptyFilters);
  const [applied, setApplied] = useState(config.emptyFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState('');
  // Said before the result count on the next successful load, e.g. "Deleted 12 error log entries."
  const pendingAnnouncementRef = useRef('');

  const load = useCallback(async () => {
    setLoading(true);
    setStatusMessage(`Loading ${config.noun}…`);
    try {
      const result = await config.load(toQuery(applied, page));
      setData(result);
      setError('');
      const announcement = pendingAnnouncementRef.current;
      pendingAnnouncementRef.current = '';
      setStatusMessage([announcement, describeResults(result.pagination, config.noun)].filter(Boolean).join(' '));
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || `Could not load ${config.noun}.`;
      // Errors stay on screen until dismissed or the next successful load (WCAG 2.2.1).
      setError(message);
      setStatusMessage('');
    } finally {
      setLoading(false);
    }
  }, [config, applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (name) => (event) => setDraft((prev) => ({ ...prev, [name]: event.target.value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (draft.from && draft.to && draft.from > draft.to) {
      setError('The "From" date must be on or before the "To" date.');
      return;
    }
    setPage(1);
    // Always a new object, so the load effect re-runs even when the filters are unchanged.
    setApplied({ ...draft });
  };

  const handleClear = () => {
    setDraft(config.emptyFilters);
    setApplied(config.emptyFilters);
    setPage(1);
  };

  const closeClearDialog = () => {
    if (clearing) return;
    setShowClearDialog(false);
    setClearError('');
  };
  const clearDialog = useDialogFocus(showClearDialog, closeClearDialog);

  const handleClearAll = async () => {
    if (clearing) return;
    setClearing(true);
    setClearError('');
    try {
      const { deleted = 0 } = await logsAPI.clearErrorLogs();
      pendingAnnouncementRef.current = `Deleted ${deleted} error log ${deleted === 1 ? 'entry' : 'entries'}.`;
      setShowClearDialog(false);
      setPage(1);
      setApplied((prev) => ({ ...prev }));
    } catch (err) {
      // Stays in the dialog until the user retries or cancels.
      setClearError(err.response?.data?.message || 'Could not clear the error logs. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const pagination = data?.pagination;
  const entries = data?.entries || [];
  const canGoBack = !loading && page > 1;
  const canGoForward = !loading && pagination && page < pagination.totalPages;

  // aria-disabled (not disabled) keeps keyboard focus on the button at the first/last page.
  const goToPage = (next, allowed) => () => {
    if (allowed) setPage(next);
  };

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <AdminHeader />
      <div className="dashboard-layout">
        <AdminSidebar active={config.sidebarKey} />
        <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
          <div className="dashboard-content system-logs">
            <h1>{config.title}</h1>
            <p className="system-logs-intro">
              {config.intro} Entries are deleted automatically after {data?.retentionDays || 90} days.
            </p>

            <form className="system-logs-filters" onSubmit={handleSubmit} aria-label={`Filter ${config.noun}`}>
              <div className="system-logs-field system-logs-field--wide">
                <label htmlFor={fieldId('q')}>Search</label>
                <input
                  id={fieldId('q')}
                  type="search"
                  className="dashboard-input"
                  value={draft.q}
                  onChange={updateDraft('q')}
                  aria-describedby={fieldId('q-hint')}
                />
                <span id={fieldId('q-hint')} className="system-logs-hint">{config.searchHint}</span>
              </div>

              {type === 'activity' && (
                <>
                  <div className="system-logs-field">
                    <label htmlFor={fieldId('role')}>Role</label>
                    <select id={fieldId('role')} className="dashboard-input" value={draft.role} onChange={updateDraft('role')}>
                      <option value="">All roles</option>
                      {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </div>
                  <div className="system-logs-field">
                    <label htmlFor={fieldId('method')}>Request type</label>
                    <select id={fieldId('method')} className="dashboard-input" value={draft.method} onChange={updateDraft('method')}>
                      <option value="">All types</option>
                      {Object.entries(METHOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                  <div className="system-logs-field">
                    <label htmlFor={fieldId('outcome')}>Result</label>
                    <select id={fieldId('outcome')} className="dashboard-input" value={draft.outcome} onChange={updateDraft('outcome')}>
                      <option value="">All results</option>
                      <option value="success">Succeeded</option>
                      <option value="failure">Failed</option>
                    </select>
                  </div>
                </>
              )}

              {type === 'errors' && (
                <div className="system-logs-field">
                  <label htmlFor={fieldId('source')}>Type</label>
                  <select id={fieldId('source')} className="dashboard-input" value={draft.source} onChange={updateDraft('source')}>
                    <option value="">All types</option>
                    {Object.entries(SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              )}

              <div className="system-logs-field">
                <label htmlFor={fieldId('from')}>From</label>
                <input id={fieldId('from')} type="date" className="dashboard-input" value={draft.from} onChange={updateDraft('from')} />
              </div>
              <div className="system-logs-field">
                <label htmlFor={fieldId('to')}>To</label>
                <input id={fieldId('to')} type="date" className="dashboard-input" value={draft.to} onChange={updateDraft('to')} />
              </div>

              <div className="system-logs-actions">
                <button type="submit" className="dashboard-button">Apply filters</button>
                <button type="button" className="dashboard-button system-logs-secondary" onClick={handleClear}>
                  Clear filters
                </button>
                <button type="button" className="dashboard-button system-logs-secondary" onClick={load}>
                  Refresh
                </button>
              </div>
            </form>

            {error && (
              <div className="system-logs-error" role="alert">
                <p>{error}</p>
                <button type="button" className="dashboard-button system-logs-secondary" onClick={() => setError('')}>
                  Dismiss<span className="sr-only"> error message</span>
                </button>
              </div>
            )}

            <div className="system-logs-toolbar">
              <p className="system-logs-status" role="status" aria-live="polite">{statusMessage}</p>
              {type === 'errors' && (
                <button
                  type="button"
                  className="dashboard-button system-logs-danger"
                  onClick={() => setShowClearDialog(true)}
                  aria-haspopup="dialog"
                >
                  Clear all error logs
                </button>
              )}
            </div>

            {entries.length > 0 && (
              // Focusable so keyboard users can scroll the table sideways at high zoom.
              <div
                className="system-logs-table-wrap"
                role="region"
                aria-labelledby={captionId}
                aria-busy={loading}
                tabIndex={0}
              >
                {type === 'activity'
                  ? <ActivityTable entries={entries} captionId={captionId} caption={config.caption} />
                  : <ErrorTable entries={entries} captionId={captionId} caption={config.caption} />}
              </div>
            )}

            {pagination && pagination.totalPages > 1 && (
              <nav className="system-logs-pagination" aria-label={`${config.title} pages`}>
                <button
                  type="button"
                  className="dashboard-button system-logs-secondary"
                  onClick={goToPage(page - 1, canGoBack)}
                  aria-disabled={!canGoBack}
                >
                  Previous page
                </button>
                <span>Page {pagination.page} of {pagination.totalPages}</span>
                <button
                  type="button"
                  className="dashboard-button system-logs-secondary"
                  onClick={goToPage(page + 1, canGoForward)}
                  aria-disabled={!canGoForward}
                >
                  Next page
                </button>
              </nav>
            )}
          </div>
        </main>
      </div>

      {showClearDialog && (
        <div className="system-logs-overlay">
          <div
            className="system-logs-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={fieldId('clear-title')}
            aria-describedby={fieldId('clear-description')}
            ref={clearDialog.dialogRef}
            tabIndex={-1}
            onKeyDown={clearDialog.onKeyDown}
          >
            <h2 id={fieldId('clear-title')}>Clear all error logs?</h2>
            <div id={fieldId('clear-description')}>
              <p>
                This deletes <strong>every</strong> error log entry
                {pagination ? ` (${pagination.total} match your current filters)` : ''}, including entries hidden by
                your filters. It cannot be undone.
              </p>
              <p>The server&apos;s own log files are not affected, and this clear is recorded in Activity Logs.</p>
            </div>
            {clearError && <p className="system-logs-dialog-error" role="alert">{clearError}</p>}
            <div className="system-logs-dialog-actions">
              <button type="button" className="dashboard-button system-logs-secondary" onClick={closeClearDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="dashboard-button system-logs-danger"
                onClick={handleClearAll}
                aria-disabled={clearing}
              >
                {clearing ? 'Clearing…' : 'Yes, delete all error logs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
